/**
 * Input handler — converts mouse/touch/keyboard events on the video element
 * into DataChannel control messages for the DeviceAgent.
 *
 * Handles coordinate mapping from the display size to the device's native
 * resolution.
 */
export class InputHandler {
    client;
    video;
    deviceWidth;
    deviceHeight;
    apiBaseUrl;
    cleanup = [];
    /**
     * Wheel throttle state (leading + trailing).
     *
     * ### Why throttle and not debounce / state-machine locking
     *
     * Earlier iterations tried to collapse "one physical gesture" into
     * "one control send" via a gathering/locked state machine. That
     * design treats trackpad frames and mouse-wheel notches uniformly
     * as *events* and drops everything after the first until enough
     * silence appears — which means a **continuous** trackpad swipe
     * (events every ~16 ms with no silence) gets permanently locked
     * after the first send and the user experiences the page as
     * completely unresponsive, even though they are actively swiping.
     * That matches user reports of *"I can't scroll after the first
     * swipe"*.
     *
     * The correct primitive is a classic leading+trailing **throttle**:
     *
     *   - The *first* event after a quiet period fires **immediately**
     *     (zero-latency leading edge for isolated clicks).
     *   - Events arriving inside the `SCROLL_THROTTLE_MS` window that
     *     follows are *not dropped* — their deltas fold into a pending
     *     accumulator so intent is never lost.
     *   - When the window ends, if the accumulator holds non-zero
     *     impulse, we emit one trailing send with that combined delta.
     *   - The *next* event then starts a fresh window, which again
     *     either triggers a leading send (if it fell outside the last
     *     window) or extends the current pending-trailing batch.
     *
     * This maps naturally to every device:
     *
     *   - Isolated mouse-wheel click  → leading send, no trailing.
     *   - Rapid wheel spin (≥12 clicks/s) → leading + periodic
     *     trailings; no clicks are lost.
     *   - Trackpad flick / continuous swipe → one send every
     *     ~`SCROLL_THROTTLE_MS` for as long as fingers/inertia keep
     *     events flowing; the stream stops naturally when the user
     *     lifts off.
     *
     * The per-event magnitude is still clamped by the server
     * (`MAX_ABS = 0.3` in `datachannel::wheel_to_scroll`), so any one
     * send can't "fling" a page-snap app past a single item. Multiple
     * quick trailing sends do produce multiple scrolls — which is the
     * desired behaviour because the user is in fact continuing to
     * input; if they want a single flip they just stop swiping.
     */
    _wheelAccDx = 0;
    _wheelAccDy = 0;
    _wheelCtxX = 0;
    _wheelCtxY = 0;
    _wheelCtxSw = 0;
    _wheelCtxSh = 0;
    _wheelLastSendAt = Number.NEGATIVE_INFINITY;
    _wheelTrailingTimer = null;
    _activeTouches = new Map();
    _ignoredTouchIds = new Set();
    /**
     * Length of one throttle window, in milliseconds. Chosen so that:
     *   - single human mouse-wheel clicks (typically ≥100 ms apart)
     *     each fall in their own window → each fires on the leading
     *     edge with zero added latency;
     *   - rapid trackpad frames (~16 ms) bundle into ~12 events per
     *     send, giving a smooth, perceptually-continuous scroll at
     *     ~12 Hz of updates on the device — above the threshold for
     *     visual-flow perception but below the rate that would saturate
     *     Douyin-style snap apps into runaway flipping.
     */
    static SCROLL_THROTTLE_MS = 80;
    _dragAmp = 2.0;
    _downX = 0;
    _downY = 0;
    constructor(options) {
        this.client = options.client;
        this.video = options.videoElement;
        this.deviceWidth = options.deviceWidth;
        this.deviceHeight = options.deviceHeight;
        this.apiBaseUrl = options.apiBaseUrl;
    }
    attach() {
        let dragging = false;
        let lastMousePoint = null;
        const sendDrag = (e, action) => {
            const sw = this.video.videoWidth || this.deviceWidth;
            const sh = this.video.videoHeight || this.deviceHeight;
            const { x: rawX, y: rawY } = this.mapCoordinates(e);
            const x = action === "down" ? rawX : this.amplifyDrag(rawX, this._downX, sw);
            const y = action === "down" ? rawY : this.amplifyDrag(rawY, this._downY, sh);
            this.client.sendControl({ type: "touch", action, x, y, pointerId: 0, screenWidth: sw, screenHeight: sh });
        };
        const onDocMouseMove = (e) => {
            if (!dragging || e.buttons !== 1)
                return;
            lastMousePoint = { clientX: e.clientX, clientY: e.clientY };
            sendDrag(e, "move");
        };
        const onDocMouseUp = (e) => {
            if (!dragging)
                return;
            dragging = false;
            lastMousePoint = null;
            document.removeEventListener("mousemove", onDocMouseMove);
            document.removeEventListener("mouseup", onDocMouseUp);
            sendDrag(e, "up");
        };
        const onMouseDown = (e) => {
            const { x, y } = this.mapCoordinates(e);
            this._downX = x;
            this._downY = y;
            lastMousePoint = { clientX: e.clientX, clientY: e.clientY };
            dragging = true;
            document.addEventListener("mousemove", onDocMouseMove);
            document.addEventListener("mouseup", onDocMouseUp);
            sendDrag(e, "down");
        };
        const onWheel = (e) => {
            e.preventDefault();
            // Normalise browser deltas into a common pixel-space baseline.
            let dx = e.deltaX;
            let dy = e.deltaY;
            if (e.deltaMode === 1) {
                // DOM_DELTA_LINE (old Firefox) — scale to a nominal px/line.
                dx *= 40;
                dy *= 40;
            }
            else if (e.deltaMode === 2) {
                // DOM_DELTA_PAGE — nearly never used, but defend against it.
                dx *= 800;
                dy *= 800;
            }
            const { x, y } = this.mapCoordinates(e);
            const sw = this.video.videoWidth || this.deviceWidth;
            const sh = this.video.videoHeight || this.deviceHeight;
            // Fold this event into the pending accumulator and keep the
            // most recent pointer / surface context (relevant for apps that
            // route scroll to the view under the cursor).
            this._wheelAccDx += dx;
            this._wheelAccDy += dy;
            this._wheelCtxX = x;
            this._wheelCtxY = y;
            this._wheelCtxSw = sw;
            this._wheelCtxSh = sh;
            const now = typeof performance !== "undefined" ? performance.now() : Date.now();
            const elapsed = now - this._wheelLastSendAt;
            if (elapsed >= InputHandler.SCROLL_THROTTLE_MS) {
                // Leading edge: either this is the very first wheel event of
                // the session or enough time has passed since the previous
                // send that we're effectively out of throttle. Dispatch now.
                this.flushWheel();
                return;
            }
            // Inside an active throttle window: the delta is already in the
            // accumulator; make sure there's a trailing timer scheduled to
            // flush at the end of the window. We only arm it once per
            // window — subsequent events in the same window just keep
            // folding their deltas in.
            if (this._wheelTrailingTimer === null) {
                const remaining = Math.max(0, InputHandler.SCROLL_THROTTLE_MS - elapsed);
                this._wheelTrailingTimer = setTimeout(() => {
                    this._wheelTrailingTimer = null;
                    this.flushWheel();
                }, remaining);
            }
        };
        const onKeyDown = (e) => {
            const keycode = mapKeyToAndroid(e.key);
            if (keycode) {
                e.preventDefault();
                this.client.sendControl({ type: "key", action: "down", keycode });
                return;
            }
            if (!e.isComposing &&
                !e.ctrlKey &&
                !e.metaKey &&
                !e.altKey &&
                e.key !== "Dead" &&
                Array.from(e.key).length === 1) {
                e.preventDefault();
                this.client.sendControl({ type: "text", content: e.key });
            }
        };
        const onKeyUp = (e) => {
            const keycode = mapKeyToAndroid(e.key);
            if (keycode) {
                this.client.sendControl({ type: "key", action: "up", keycode });
            }
        };
        const onTouchStart = (e) => {
            e.preventDefault();
            const sw = this.video.videoWidth || this.deviceWidth;
            const sh = this.video.videoHeight || this.deviceHeight;
            for (const touch of Array.from(e.changedTouches)) {
                this._ignoredTouchIds.delete(touch.identifier);
                this._activeTouches.set(touch.identifier, {
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                });
                const { x, y } = this.mapTouch(touch);
                this.client.sendControl({
                    type: "touch", action: "down", x, y,
                    pointerId: touch.identifier, pressure: touch.force || 1.0,
                    screenWidth: sw, screenHeight: sh,
                });
            }
        };
        const onTouchEnd = (e) => {
            const sw = this.video.videoWidth || this.deviceWidth;
            const sh = this.video.videoHeight || this.deviceHeight;
            for (const touch of Array.from(e.changedTouches)) {
                if (this._ignoredTouchIds.delete(touch.identifier))
                    continue;
                if (!this._activeTouches.has(touch.identifier))
                    continue;
                this._activeTouches.delete(touch.identifier);
                const { x, y } = this.mapTouch(touch);
                this.client.sendControl({
                    type: "touch", action: "up", x, y,
                    pointerId: touch.identifier, pressure: 0,
                    screenWidth: sw, screenHeight: sh,
                });
            }
        };
        const onTouchMove = (e) => {
            e.preventDefault();
            const sw = this.video.videoWidth || this.deviceWidth;
            const sh = this.video.videoHeight || this.deviceHeight;
            for (const touch of Array.from(e.changedTouches)) {
                if (this._ignoredTouchIds.has(touch.identifier))
                    continue;
                if (!this._activeTouches.has(touch.identifier))
                    continue;
                this._activeTouches.set(touch.identifier, {
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                });
                const { x, y } = this.mapTouch(touch);
                this.client.sendControl({
                    type: "touch", action: "move", x, y,
                    pointerId: touch.identifier, pressure: touch.force || 1.0,
                    screenWidth: sw, screenHeight: sh,
                });
            }
        };
        // APK drag-and-drop upload via REST API
        const onDrop = (e) => {
            e.preventDefault();
            const file = e.dataTransfer?.files[0];
            if (file && file.name.endsWith(".apk") && this.apiBaseUrl) {
                const form = new FormData();
                form.append("apk", file);
                fetch(`${this.apiBaseUrl}/device/install-apk`, { method: "POST", body: form })
                    .then((resp) => {
                    if (!resp.ok)
                        console.error("[InputHandler] APK install failed:", resp.status);
                    else
                        console.log("[InputHandler] APK installed:", file.name);
                })
                    .catch((err) => console.error("[InputHandler] APK upload error:", err));
            }
            else if (file && file.name.endsWith(".apk")) {
                console.warn("[InputHandler] APK dropped but no apiBaseUrl configured");
            }
        };
        const onDragOver = (e) => e.preventDefault();
        const onContextMenu = (e) => e.preventDefault();
        const onCompositionEnd = (e) => {
            if (e.data)
                this.client.sendControl({ type: "text", content: e.data });
        };
        const onPaste = (e) => {
            const text = e.clipboardData?.getData("text/plain") ?? "";
            if (!text)
                return;
            e.preventDefault();
            this.client.sendControl({ type: "text", content: text });
        };
        // A scrcpy orientation change swaps the decoded frame dimensions while
        // the user's pointer may still be down. Continuing that gesture would
        // mix the old and new coordinate spaces. Release it against the new
        // geometry, then ignore its remaining move/up events until the browser
        // starts a fresh gesture.
        const onVideoResize = () => {
            const sw = this.video.videoWidth || this.deviceWidth;
            const sh = this.video.videoHeight || this.deviceHeight;
            if (dragging && lastMousePoint) {
                const { x, y } = this.mapClientCoordinates(lastMousePoint.clientX, lastMousePoint.clientY);
                this.client.sendControl({
                    type: "touch",
                    action: "up",
                    x,
                    y,
                    pointerId: 0,
                    pressure: 0,
                    screenWidth: sw,
                    screenHeight: sh,
                });
            }
            dragging = false;
            lastMousePoint = null;
            document.removeEventListener("mousemove", onDocMouseMove);
            document.removeEventListener("mouseup", onDocMouseUp);
            for (const [pointerId, point] of this._activeTouches) {
                const { x, y } = this.mapClientCoordinates(point.clientX, point.clientY);
                this.client.sendControl({
                    type: "touch",
                    action: "up",
                    x,
                    y,
                    pointerId,
                    pressure: 0,
                    screenWidth: sw,
                    screenHeight: sh,
                });
                this._ignoredTouchIds.add(pointerId);
            }
            this._activeTouches.clear();
            this.clearPendingWheel();
        };
        this.video.addEventListener("mousedown", onMouseDown);
        this.video.addEventListener("wheel", onWheel, { passive: false });
        this.video.addEventListener("keydown", onKeyDown);
        this.video.addEventListener("keyup", onKeyUp);
        this.video.addEventListener("touchstart", onTouchStart, { passive: false });
        this.video.addEventListener("touchend", onTouchEnd);
        this.video.addEventListener("touchmove", onTouchMove, { passive: false });
        this.video.addEventListener("contextmenu", onContextMenu);
        this.video.addEventListener("drop", onDrop);
        this.video.addEventListener("dragover", onDragOver);
        this.video.addEventListener("resize", onVideoResize);
        this.video.addEventListener("compositionend", onCompositionEnd);
        this.video.addEventListener("paste", onPaste);
        this.video.tabIndex = 0;
        this.cleanup = [
            () => {
                dragging = false;
                document.removeEventListener("mousemove", onDocMouseMove);
                document.removeEventListener("mouseup", onDocMouseUp);
                this.video.removeEventListener("mousedown", onMouseDown);
            },
            () => this.video.removeEventListener("wheel", onWheel),
            () => this.video.removeEventListener("keydown", onKeyDown),
            () => this.video.removeEventListener("keyup", onKeyUp),
            () => this.video.removeEventListener("touchstart", onTouchStart),
            () => this.video.removeEventListener("touchend", onTouchEnd),
            () => this.video.removeEventListener("touchmove", onTouchMove),
            () => this.video.removeEventListener("contextmenu", onContextMenu),
            () => this.video.removeEventListener("drop", onDrop),
            () => this.video.removeEventListener("dragover", onDragOver),
            () => this.video.removeEventListener("resize", onVideoResize),
            () => this.video.removeEventListener("compositionend", onCompositionEnd),
            () => this.video.removeEventListener("paste", onPaste),
        ];
    }
    detach() {
        for (const fn of this.cleanup)
            fn();
        this.cleanup = [];
        this.clearPendingWheel();
        this._activeTouches.clear();
        this._ignoredTouchIds.clear();
    }
    clearPendingWheel() {
        if (this._wheelTrailingTimer !== null) {
            clearTimeout(this._wheelTrailingTimer);
            this._wheelTrailingTimer = null;
        }
        this._wheelAccDx = 0;
        this._wheelAccDy = 0;
        this._wheelLastSendAt = Number.NEGATIVE_INFINITY;
    }
    /**
     * Emit the pending accumulator as a single `scroll` control message.
     * Called both on the leading edge (directly from `onWheel` when the
     * throttle window has elapsed) and on the trailing edge (via the
     * timer scheduled while inside a window). Marks `_wheelLastSendAt`
     * so the next leading-edge check fires cleanly.
     */
    flushWheel() {
        if (this._wheelAccDx === 0 && this._wheelAccDy === 0)
            return;
        this.client.sendControl({
            type: "scroll",
            x: this._wheelCtxX,
            y: this._wheelCtxY,
            dx: this._wheelAccDx,
            dy: this._wheelAccDy,
            screenWidth: this._wheelCtxSw,
            screenHeight: this._wheelCtxSh,
        });
        this._wheelAccDx = 0;
        this._wheelAccDy = 0;
        this._wheelLastSendAt =
            typeof performance !== "undefined" ? performance.now() : Date.now();
    }
    updateDeviceResolution(width, height) {
        this.deviceWidth = width;
        this.deviceHeight = height;
    }
    /**
     * Compute the visible content rect inside the video element, accounting for
     * object-fit: contain letterboxing/pillarboxing.
     */
    contentRect() {
        const rect = this.video.getBoundingClientRect();
        const vw = this.video.videoWidth;
        const vh = this.video.videoHeight;
        if (!vw || !vh)
            return { offsetX: 0, offsetY: 0, width: rect.width, height: rect.height };
        const videoRatio = vw / vh;
        const elemRatio = rect.width / rect.height;
        if (videoRatio > elemRatio) {
            const contentW = rect.width;
            const contentH = rect.width / videoRatio;
            return { offsetX: 0, offsetY: (rect.height - contentH) / 2, width: contentW, height: contentH };
        }
        const contentH = rect.height;
        const contentW = rect.height * videoRatio;
        return { offsetX: (rect.width - contentW) / 2, offsetY: 0, width: contentW, height: contentH };
    }
    /**
     * Map mouse position to scrcpy video-stream coordinates.
     * We use video.videoWidth/videoHeight (the actual decoded resolution)
     * because scrcpy's PositionMapper rejects events whose screenSize
     * doesn't match the video stream size.
     */
    mapCoordinates(e) {
        return this.mapClientCoordinates(e.clientX, e.clientY);
    }
    mapClientCoordinates(clientX, clientY) {
        const rect = this.video.getBoundingClientRect();
        const c = this.contentRect();
        const relX = (clientX - rect.left - c.offsetX) / c.width;
        const relY = (clientY - rect.top - c.offsetY) / c.height;
        const w = this.video.videoWidth || this.deviceWidth;
        const h = this.video.videoHeight || this.deviceHeight;
        return {
            x: Math.round(Math.max(0, Math.min(1, relX)) * w),
            y: Math.round(Math.max(0, Math.min(1, relY)) * h),
        };
    }
    amplifyDrag(raw, origin, max) {
        return Math.round(Math.max(0, Math.min(max, origin + (raw - origin) * this._dragAmp)));
    }
    mapTouch(touch) {
        return this.mapClientCoordinates(touch.clientX, touch.clientY);
    }
}
function mapKeyToAndroid(key) {
    const map = {
        Backspace: "KEYCODE_DEL",
        Enter: "KEYCODE_ENTER",
        Escape: "KEYCODE_BACK",
        Tab: "KEYCODE_TAB",
        ArrowUp: "KEYCODE_DPAD_UP",
        ArrowDown: "KEYCODE_DPAD_DOWN",
        ArrowLeft: "KEYCODE_DPAD_LEFT",
        ArrowRight: "KEYCODE_DPAD_RIGHT",
        Home: "KEYCODE_HOME",
        " ": "KEYCODE_SPACE",
    };
    return map[key] ?? null;
}
